// Pure event → readable-change mapping for the History screen. No DOM here
// (that lives in screens/history.ts) so the wording, formatting and diffing
// are testable without a browser. Audit internals that would only confuse
// (idempotency keys, request hashes) are dropped at this boundary — they
// never reach the product UI.

import { extraWords, extrasOf } from './extras-data'
import { COPIED_TEXT } from './copied'
import { clockWords, roundConfig, MONTHLY_DAYS, monthlyCycles, postcodeWords, shownTime } from './round-config'
import { setTimesOn } from './business-settings'
import { blockedReasonLabel, dayLabel, money } from './ui'

// The shape of a row from GET /api/events. request_hash/idempotency_key are
// deliberately absent: this interface is the whole contract the UI sees.
export interface AuditEvent {
  on_invoice?: boolean
  id: number
  actor: string
  entity: string
  entity_id: number
  action: string
  before_json: string | null
  after_json: string | null
  created_at: string
}

export const ENTITIES: readonly string[] = ['customers', 'cleans', 'leads', 'day_notes', 'day_ledger', 'gutter_jobs', 'media']

// The History filter's types. A customer's extra jobs (0042) are a type of their own only on a round with several
// jobs switched on: every other round's filter stays exactly as it was. Their rows are still worded (ENTITY_LABELS).
export function historyEntities(severalJobs: boolean): readonly string[] {
  return severalJobs ? [...ENTITIES, 'customer_jobs'] : ENTITIES
}

const ENTITY_LABELS: Record<string, string> = {
  customers: 'Customer',
  cleans: 'Clean',
  leads: 'New job',
  lead_quotes: 'Quote',
  day_notes: 'Day note',
  day_ledger: 'Day ledger',
  gutter_jobs: 'Gutter job',
  media: 'Photo/note',
  // A customer's extra job (0042).
  customer_jobs: 'Job',
  extra_job_offers: 'Extra offer',
  customer_offer_agreements: 'Offer agreement',
  customer_job_ideas: 'Job idea',
  lead_messages: 'Message',
  enquiry_regular_offers: 'Regular-clean offer',
  price_reviews: 'Price review',
  price_review_items: 'Price notice',
}

export function entityLabel(entity: string): string {
  return ENTITY_LABELS[entity] ?? entity
}

export function actionWord(action: string): string {
  if (action === 'create') return 'Added'
  if (action === 'update') return 'Edited'
  if (action === 'delete') return 'Removed'
  return action.charAt(0).toUpperCase() + action.slice(1)
}

// Bookkeeping every mutation bumps — real field changes only in the list.
const META_FIELDS = new Set(['_breakdown_rev', '_breakdown_paid', 'id', 'version', 'created_at', 'updated_at', 'created_by', 'request_hash', 'idempotency_key', 'business_id', '_undo', '_customer_offer', '_withdrawn_message_id', 'offer_reply_answer', 'price_set_at', 'price_generation', '_attempt', '_also', 'review_id', 'creator', 'evidence_json', 'protected_json', 'send_key', 'application_key', 'message_id', 'undo_json'])

const FIELD_LABELS: Record<string, string> = {
  number: 'Quote number', revision: 'Revision', total_pence: 'Total', sent_way: 'Sent by', lead_id: 'Enquiry',
  old_pence: 'Old price', new_pence: 'New price', effective_date: 'Start date', notice: 'Price notice',
  send_state: 'Notice', reply_state: 'Their reply', application_state: 'Price change', send_channel: 'Sent by',
  sent_at: 'Sent', applied_at: 'Price changed',
  name: 'Name',
  address: 'Address',
  postcode: 'Postcode',
  // Tidy-up 2 item 3 (20 Oct 2026): History read "message by" and "none" (build-messages.md 2).
  postcode_enough: 'Postcode is enough',
  message_by: "How they're messaged",
  // Commercial customers (0080, 21 Oct 2026; build-money): it read "commercial" with 1 and 0.
  commercial: 'Commercial (invoice after each job)',
  maps_link: 'Map link',
  phone: 'Phone',
  origin: 'Came from',
  tier: 'Tier',
  area: 'Area',
  date: 'Date',
  quoted_on: 'Quote date',
  amount_pence: 'Price',
  reg_price_pence: 'Regular price',
  inside_price_pence: 'Inside price',
  first_clean_price_pence: 'First clean price',
  initial_pence: 'Initial price',
  regular_pence: 'Regular price',
  gross_pence: 'Gross',
  worker_cut_pence: 'Worker cut',
  our_cut_pence: 'Our cut',
  breakdown_json: 'Price breakdown',
  cycle_days: 'Cycle',
  cycle_weeks: 'Cycle (weeks)',
  cycle_text: 'Cycle note',
  regular_offer_choice_id: 'Cleaning choice',
  wa_offer_cycle_a: 'First cycle on WhatsApp buttons',
  wa_offer_cycle_b: 'Second cycle on WhatsApp buttons',
  time_taken: 'Time taken',
  job_notes: 'Job notes',
  parking_restrictions: 'Parking',
  preferred_times: 'Preferred times',
  call_notes: 'Call notes',
  // Contact/follow-up (migration 0006) — distinct from Call notes on purpose:
  // history must show which of the two a person edited.
  last_contacted_on: 'Last contacted',
  contact_note: 'Contact note',
  follow_up_on: 'Follow up on',
  to_call: 'To call',
  payment_increase: 'Payment increase',
  maybe_lapsed: 'Maybe lapsed',
  status: 'Status',
  status_raw: 'Status',
  state: 'State',
  kind: 'Kind',
  payment_status: 'Payment',
  payment_method: 'Payment method',
  confirmed: 'Confirmed',
  chased: 'Chased',
  checked: 'Checked',
  time_slot: 'Time slot',
  duration_minutes: 'Duration',
  sort_order: 'Order',
  worker: 'Worker',
  note: 'Note',
  notes: 'Notes',
  notes2: 'Notes 2',
  // "Couldn't do it" outcome (migration 0008)
  blocked_reason: "Couldn't do it",
  blocked_note: 'Reason note',
  blocked_at: 'Recorded at',
  initial_lead: 'Initial lead',
  source: 'Source',
  customer_id: 'Customer',
  converted_customer_id: 'Linked customer',
  deleted_at: 'Deleted at',
  started_at: 'Started',
  finished_at: 'Finished',
  // How each was got (0083).
  started_how: 'Start time',
  finished_how: 'Finish time',
  outsides_done_at: 'Outsides done',
  part_inside_of_pence: 'Whole inside price',
  // Extras for this visit (0087): named, not shown as their raw list (the detail comes in part 2).
  extras_json: 'Extras',
  // Warning or just a note (0088).
  note_kinds: 'Warning or just a note',
  cash_collected_pence: 'Cash taken',
  chase_count: 'Chases sent',
  chased_on: 'Last chased',
  chase_called_at: 'Called about paying',
  reviewed_on: "Marked didn't happen",
  rebook_messages: 'Rebook messages sent',
  rebook_messaged_on: 'Last rebook message',
  enquiry_messages: 'Messages sent',
  enquiry_messaged_on: 'Last message',
  latitude: 'Map position',
  longitude: 'Map position',
  geocoded_postcode: 'Map postcode',
  email: 'Sign-in email',
  role: 'Role',
  worker_name: 'Name on visits',
  show_prices: 'Sees prices',
  photo: 'Photo',
  photo_key: 'Photo',
  // Several jobs (0042).
  price_pence: 'Price',
  job_id: 'Job',
  job_name: 'Job name',
  job_stopped_on: 'Stopped',
  stopped_on: 'Stopped',
}

export function fieldLabel(field: string): string {
  return postcodeWords(FIELD_LABELS[field] ?? field.replace(/_/g, ' '))
}

// 0/1 flags rendered as words; money as £; yyyy-mm-dd as the sheet's day
// style; datetimes as day + clock. Blank stays visibly blank — a cleared
// cell is a real change, not a missing one.
const YES_NO_FIELDS = new Set(['confirmed', 'chased', 'checked', 'to_call', 'maybe_lapsed', 'show_prices', 'postcode_enough', 'commercial'])
// A customer's way (0036) as the way menu words it; none of their own is the round's usual.
const WAY_WORDS: Record<string, string> = { whatsapp: 'WhatsApp', text: 'Text', email: 'Email', none: "Don't message" }
// How a job's start or finish time was got (0083), in the words Today uses.
const HOW_WORDS: Record<string, string> = { tap: 'tapped at the time', noticed: 'when the phone noticed', said: 'given by hand', rough: 'rough', unsure: 'not known', guessed: 'from the booking, not changed' }

export function formatValue(field: string, v: unknown): string {
  if (field === 'send_state') return ({draft:'Not sent',sending:'Sending',sent:'Sent',failed:'It did not go',unknown:'Not sure it went'} as Record<string,string>)[String(v)] ?? '(blank)'
  if (field === 'reply_state') return ({none:'No reply',ok:'Said OK',no:'Said no'} as Record<string,string>)[String(v)] ?? '(blank)'
  if (field === 'application_state') return ({pending:'Waiting for the start date',applied:'Price changed',cancelled:'Rise cancelled',conflict:'Needs checking',undone:'Price change undone'} as Record<string,string>)[String(v)] ?? '(blank)'
  if (field === 'send_channel') return ({roundly_text:'Roundly text',copy:COPIED_TEXT,text:'Text',whatsapp:'WhatsApp',email:'Email'} as Record<string,string>)[String(v)] ?? '(blank)'
  if (field === 'message_by') return v === null || v === undefined || v === '' ? 'Your usual' : WAY_WORDS[String(v)] ?? String(v)
  if (field === 'started_how' || field === 'finished_how') return v === null || v === undefined || v === '' ? '(blank)' : HOW_WORDS[String(v)] ?? String(v)
  // Extras (0087): "Conservatory roof · 4 panels £60", or none.
  // Warning or just a note (0088): "Job notes: Just a note", never the stored keys (Sonnet's notes review 9).
  if (field === 'note_kinds') {
    if (v === null || v === undefined || v === '') return "Roundly's guess"
    try {
      const names: Record<string, string> = { job_notes: 'Job notes', parking_restrictions: 'Parking', preferred_times: 'Preferred times', call_notes: 'When calling' }
      return Object.entries(JSON.parse(String(v)) as Record<string, { kind?: string }>).map(([k, c]) => `${names[k] ?? k}: ${c?.kind === 'warn' ? 'Warning' : 'Just a note'}`).join(', ') || "Roundly's guess"
    } catch { return "Roundly's guess" }
  }
  if (field === 'breakdown_json') {
    if (v == null || v === '') return 'none'
    try { return (JSON.parse(String(v)) as { name: string; count: number; each_pence: number }[]).map(l => `${l.name} · ${l.count} at ${money(l.each_pence)} = ${money(l.count * l.each_pence)}`).join(', ') || 'none' } catch { return 'none' }
  }
  if (field === 'extras_json') return v === null || v === undefined || v === '' ? 'none' : extrasOf(String(v)).map(extraWords).join(', ') || 'none'
  if (v === null || v === undefined || v === '') return '(blank)'
  if (field.endsWith('_pence')) return typeof v === 'number' ? money(v) : String(v)
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  if (field === 'blocked_reason') return blockedReasonLabel(String(v))
  // A picture's storage name means nothing to a person: say whether there is one.
  if (field === 'photo_key') return 'uploaded'
  if (YES_NO_FIELDS.has(field) && (v === 0 || v === 1)) return v === 1 ? 'yes' : 'no'
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/.test(v)) {
    // A time with it is a UTC stamp (started_at, finished_at, blocked_at…): the round's day and
    // clock, the same as the heading above it (Astra review, 29 Sep 2026).
    return v.length > 10 ? eventTime(v) : dayLabel(v.slice(0, 10))
  }
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export function parseRow(json: string | null): Record<string, unknown> | null {
  if (!json) return null
  try {
    const v: unknown = JSON.parse(json)
    return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export interface FieldChange {
  field: string
  label: string
  from: string
  to: string
}

// What a person needs to read: for an edit, the fields that actually moved
// (before → after); for an add, the values that were set; for a removal,
// the values that were there. Bookkeeping bumps are skipped.
export function fieldChanges(action: string, before: Record<string, unknown> | null, after: Record<string, unknown> | null): FieldChange[] {
  const out: FieldChange[] = []
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])
  for (const k of keys) {
    if (META_FIELDS.has(k)) continue
    // New jobs carries both the selected label and its internal grouping.
    // Show the label once; two different "Status" rows obscure one edit.
    if (k === 'status' && keys.has('status_raw')) continue
    const from = before?.[k]
    const to = after?.[k]
    if (action === 'create') {
      if (to === null || to === undefined || to === '') continue
    } else if (action === 'delete') {
      if (from === null || from === undefined || from === '') continue
    } else if (from === to || (after !== null && !(k in after))) {
      // An update that did not store a field did not change it.
      continue
    }
    out.push({ field: k, label: fieldLabel(k), from: formatValue(k, from), to: formatValue(k, to) })
  }
  out.sort((a, b) => a.label.localeCompare(b.label))
  return out
}

// The row's own name when the event carries one (customers, leads), the
// date when the row has one (cleans, notes, ledger) — otherwise the plain
// record ID, so an entry is always identifiable without raw metadata.
export function recordTitle(entity: string, entityId: number, before: Record<string, unknown> | null, after: Record<string, unknown> | null): string {
  const row = after ?? before
  const name = row?.name
  if (typeof name === 'string' && name.trim() !== '') return name
  if (entity === 'lead_quotes' && row?.number) return `${row.number} · Revision ${row.revision}`
  if (typeof row?.date === 'string' && row.date) return `#${entityId} · ${dayLabel(row.date.slice(0, 10))}`
  return `#${entityId}`
}

// D1 timestamps are 'yyyy-mm-dd HH:MM:SS' UTC. Rendered in UK time, day and
// clock both, the way everything else in Roundly reads: "13:19 UTC" was an hour
// behind the clock on the wall all summer (Liam's Dropby look-over, 29 Sep
// 2026). The day comes from the UK clock too, so 23:30 UTC in summer is the
// next morning's 00:30.
// On the round's clock since Step 1 (round-config.ts): London for every UK round.
const STAMPS = new Map<string, Intl.DateTimeFormat>()
function stampFormat(timeZone: string): Intl.DateTimeFormat {
  let f = STAMPS.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    STAMPS.set(timeZone, f)
  }
  return f
}
export function eventTime(created: string): string {
  const at = eventDayClock(created)
  return at ? `${dayLabel(at.day)}, ${at.clock}` : created
}
// The round's day (yyyy-mm-dd) and clock of a change: History groups its changes under their day (design A, 23 Oct
// 2026). Null when the stamp cannot be read.
export function eventDayClock(created: string): { day: string; clock: string } | null {
  const d = new Date(`${created.slice(0, 10)}T${created.length > 10 ? created.slice(11, 19) : '00:00:00'}Z`)
  if (Number.isNaN(d.getTime())) return null
  const parts = stampFormat(roundConfig().timeZone).formatToParts(d)
  const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? ''
  return { day: `${part('year')}-${part('month')}-${part('day')}`, clock: clockWords(Number(part('hour')), part('minute')) }
}

// ---- plain sentences (Liam, 2026-09-25) -------------------------------------
// Each entry leads with one sentence a person would say out loud: "Lewis
// marked Anna's visit on Tue 9 Sep paid by bank", "Liam added Sendi as a
// worker, with prices hidden". The exact before and after values stay one tap
// away under it (screens/history.ts), so nothing the old list showed is lost.

// The names the server sends with a page of history: the customer behind each
// visit (by customer id) and the name each login goes by in People (by email).
export interface HistoryNames {
  customers: Record<string, string>
  people: Record<string, string>
  // A visit's customer and date by visit id, for entries that stored neither.
  visits?: Record<string, { customer: string | null; date: string }>
}
export const NO_NAMES: HistoryNames = { customers: {}, people: {}, visits: {} }

// Shared inboxes say nothing about who was holding the phone, so they are
// never turned into a first name.
const SHARED_MAILBOXES = new Set(['hello', 'info', 'admin', 'office', 'contact', 'team', 'enquiries', 'enquiry', 'bookings', 'booking', 'mail', 'accounts', 'sales', 'support', 'jobs', 'work', 'help', 'dev', 'quotes', 'quote', 'orders', 'noreply', 'reception', 'hey', 'service', 'bookkeeping'])

// Who made the change: their name in People; failing that the first part of a
// personal email ("lewis@…" is Lewis); failing that the email itself. A note
// the change was saved with (a spreadsheet fix: "email via orchestrator:
// why") comes back apart, so the sentence stays short and the note still shows.
export function whoDidIt(actor: string, names: HistoryNames = NO_NAMES): { who: string; email: string | null; note: string | null } {
  const text = String(actor ?? '').trim()
  const match = /[^\s@()]+@[^\s@()]+\.[^\s@():]+/.exec(text)
  // The first spreadsheet import and service logins are the app, not a person.
  if (!match) return { who: text === 'importer' || text.startsWith('service:') ? 'Roundly' : text && text !== 'unknown' ? text : 'Someone', email: null, note: null }
  const email = match[0]
  let rest = (text.slice(0, match.index) + text.slice(match.index + email.length)).trim()
  if (rest.startsWith('(') && rest.endsWith(')')) rest = rest.slice(1, -1).trim()
  rest = rest.replace(/^via orchestrator:\s*/i, '')
  const known = names.people[email.toLowerCase()]
  const local = email.split('@')[0]
  // Initials ("lj@") are too short to be a name, so they stay as the email.
  const who = known ?? (/^[a-z]{3,}$/i.test(local) && !SHARED_MAILBOXES.has(local.toLowerCase()) ? local.charAt(0).toUpperCase() + local.slice(1).toLowerCase() : email)
  return { who, email, note: rest || null }
}

const blank = (v: unknown): boolean => v === null || v === undefined || v === ''
const str = (v: unknown): string => (blank(v) ? '' : String(v))
const lower = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1)
const possessive = (name: string): string => `${name}'s`
const day = (v: unknown): string => (typeof v === 'string' && v.length >= 10 ? dayLabel(v.slice(0, 10)) : '')
const pounds = (v: unknown): string => (typeof v === 'number' ? money(v) : '')
const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`

function cycleWords(days: unknown): string {
  if (typeof days !== 'number' || days <= 0) return ''
  if (days === MONTHLY_DAYS && monthlyCycles()) return 'every month'
  return days % 7 === 0 ? `every ${plural(days / 7, 'week')}` : `every ${plural(days, 'day')}`
}

// One sentence for one change. `before`/`after` are the parsed snapshots.
export function eventSentence(ev: Pick<AuditEvent, 'actor' | 'entity' | 'entity_id' | 'action'>, before: Record<string, unknown> | null, after: Record<string, unknown> | null, names: HistoryNames = NO_NAMES): string {
  const { who } = whoDidIt(ev.actor, names)
  const all = fieldChanges(ev.action, before, after)
  const { text, covers, subject } = phrase(ev.entity, ev.entity_id, ev.action, before, after, names)
  // Anything else saved in the same tap is counted, so the sentence never
  // hides that more changed: the exact list is under it.
  const others = covers === 'all' ? 0 : all.filter((c) => !covers.has(c.field)).length
  return `${subject ?? who} ${text}${others > 0 ? `, plus ${plural(others, 'other change')}` : ''}`
}

type Phrase = { text: string; covers: Set<string> | 'all'; subject?: string }
const says = (text: string, ...fields: string[]): Phrase => ({ text, covers: new Set(fields) })
const whole = (text: string): Phrase => ({ text, covers: 'all' })

function phrase(entity: string, entityId: number, action: string, before: Record<string, unknown> | null, after: Record<string, unknown> | null, names: HistoryNames): Phrase {
  // Some entries store only the fields that were saved afterwards, so the
  // visit's customer and date come from whichever snapshot has them.
  const row: Record<string, unknown> = { ...(before ?? {}), ...(after ?? {}) }
  // A field counts as moved only when the later snapshot carries it: some
  // entries store just the fields that were saved.
  const moved = (k: string): boolean => action === 'update' && !!after && k in after && before?.[k] !== after[k]
  const was = (k: string): unknown => before?.[k]
  const now = (k: string): unknown => after?.[k]
  const other = (fields: string[]): string => fields.map((f) => lower(fieldLabel(f))).join(' and ')

  if (entity === 'lead_quotes') return whole(`${row.sent_at ? 'sent' : 'made'} quote ${row.number}, revision ${row.revision}, for ${money(Number(row.total_pence))}`)
  if (entity === 'enquiry_regular_offers') return whole(row.state === 'withdrawn' ? 'closed an offer of regular cleans' : 'offered regular cleans')
  if (entity === 'customer_offer_agreements' && row.offer_reply_answer) return whole((row.offer_reply_answer as {outcome:string}).outcome==='not_no'?'confirmed their reply was not a no to offers':'confirmed their reply asked to stop offers')
  if (entity === 'customer_offer_agreements') return whole(row.state === 'agreed' ? 'recorded agreement to hear about other jobs' : row.state === 'revoked' ? 'revoked agreement to hear about other jobs' : row.state === 'not_asked' ? 'recorded other jobs as not asked yet' : 'asked about hearing of other jobs')
  if (entity === 'lead_messages' && action==='delete')return whole('recorded that an offer message was not sent')
  if (entity === 'customer_job_ideas') return whole('flagged a note as a job idea')
  if (entity === 'extra_job_offers') {
    if (row.customer_id) {
      if(row._withdrawn_message_id)return whole('recorded the offer was not sent and withdrew its message')
      if (moved('booked_visit_id')) return whole(row.booked_visit_id ? 'added the accepted extra jobs to one visit' : 'undid the extra jobs added to a visit')
      if (moved('acceptance_state')) return whole(row.acceptance_state === 'none' ? 'recorded no thanks to the offered jobs' : 'recorded yes to the chosen extra jobs')
      if (moved('send_state') && row.send_state === 'sent') return whole('confirmed the offer of another job was sent')
      if (action === 'create') return whole(row.send_state === 'sent' ? 'recorded an offer of another job sent by the owner' : 'opened an offer of another job, not confirmed sent')
    }
    if (moved('send_state') && row.send_state === 'withdrawn') return whole('withdrew an offer of extra work')
    if (moved('acceptance_state')) return whole(row.acceptance_state === 'waiting' ? 'put the offered extras back to waiting' : row.acceptance_state === 'none' ? 'recorded that no offered extras were accepted' : 'recorded the accepted extras for the first visit')
    return whole('saved an offer of extra work')
  }
  if (entity === 'price_reviews') return whole('started a price review')
  if (entity === 'price_review_items') {
    const customer=names.customers[String(row.customer_id)] || 'a customer'
    if (moved('application_state')) return whole(row.application_state==='applied' ? `applied the price rise for ${customer}` : row.application_state==='undone' ? `undid the price rise for ${customer}` : row.application_state==='cancelled' ? `cancelled the price rise for ${customer}` : `marked the price rise for ${customer} to check`)
    if (moved('reply_state')) return whole(row.reply_state==='no' ? `recorded that ${customer} said no to the price rise` : row.reply_state==='ok' ? `recorded that ${customer} said OK to the price rise` : `recorded no reply to the price notice for ${customer}`)
    if (moved('send_state')) return whole(row.send_state==='sent' ? `confirmed the price notice to ${customer} was sent` : row.send_state==='sending' ? `started sending the price notice to ${customer}` : row.send_state==='failed' ? `recorded that the price notice to ${customer} did not go` : `prepared the price notice for ${customer}, not confirmed sent`)
    return whole(`saved the price notice for ${customer}`)
  }

  if (entity === 'cleans') {
    const known = names.visits?.[String(entityId)]
    const customer = names.customers[str(row.customer_id)] ?? known?.customer ?? undefined
    const when = day(row.date ?? known?.date)
    const visit = `${customer ? possessive(customer) : 'a'} visit${when ? ` on ${when}` : ''}`
    const paidHow = (m: unknown): string => (m === 'bank' ? ' by bank' : m === 'cash' ? ' in cash' : '')
    if (action === 'create') {
      const worker = str(row.worker)
      // After a visit is marked done, the app pencils in the next one itself.
      if (row.source === 'suggested') return { ...whole(`pencilled in ${customer ? possessive(customer) : 'the'} next visit${when ? ` for ${when}` : ''}`), subject: 'Roundly' }
      if (row.source === 'tentative') return { ...whole(`pencilled in a check-in with ${customer ?? 'a customer'}${when ? ` for ${when}` : ''}`), subject: 'Roundly' }
      if (row.source === 'week') return whole(`put ${customer ?? 'a visit'} in the week of ${when || 'a week'}${worker ? ` with ${worker}` : ''}`)
      if (row.state === 'done') return whole(`added a done visit for ${customer ?? 'a customer'}${when ? ` on ${when}` : ''}${row.payment_status === 'paid' ? `, paid${paidHow(row.payment_method)}` : ''}`)
      return whole(`booked ${customer ?? 'a visit'}${when ? ` for ${when}` : ''}${worker ? ` with ${worker}` : ''}`)
    }
    if (action === 'delete') return whole(`removed ${visit}`)
    if (action === 'import_annotation') return whole(`corrected ${visit} from the spreadsheet`)
    if (moved('deleted_at')) return says(blank(now('deleted_at')) ? `put back ${visit}` : `removed ${visit}`, 'deleted_at')
    if (moved('state')) {
      const s = now('state')
      if (s === 'done') {
        const p = now('payment_status')
        const pay = moved('payment_status') && p === 'paid' ? ` and paid${paidHow(now('payment_method'))}` : moved('payment_status') && p === 'unpaid' ? ' and not paid yet' : ''
        return says(`marked ${visit} done${pay}`, 'state', 'payment_status', 'payment_method', 'finished_at', 'cash_collected_pence', 'started_at', 'confirmed')
      }
      if (s === 'cancelled') return says(`cancelled ${visit}`, 'state', 'confirmed', 'payment_status', 'payment_method')
      return says(`put ${visit} back to booked`, 'state', 'payment_status', 'payment_method', 'finished_at', 'cash_collected_pence')
    }
    if (moved('date')) {
      const from = day(was('date'))
      const to = now('source') === 'week' ? `the week of ${day(now('date'))}` : day(now('date'))
      const agreed = moved('confirmed') && now('confirmed') === 1 ? ', agreed with the customer' : ''
      return says(`moved ${customer ? possessive(customer) : 'a'} visit${from ? ` from ${from}` : ''} to ${to}${agreed}`, 'date', 'source', 'reviewed_on', 'blocked_reason', 'blocked_note', 'blocked_at', 'confirmed')
    }
    if (moved('breakdown_json')) return says(now('breakdown_json') ? `added a price breakdown to ${visit}` : `removed the price breakdown from ${visit}`, 'breakdown_json', 'amount_pence')
    if (moved('payment_status')) {
      const p = now('payment_status')
      if (p === 'paid') return says(`marked ${visit} paid${paidHow(now('payment_method'))}`, 'payment_status', 'payment_method', 'chased')
      if (p === 'unpaid') return says(`marked ${visit} not paid`, 'payment_status', 'payment_method', 'chased')
      return says(`set ${visit} back to payment not known`, 'payment_status', 'payment_method', 'chased')
    }
    if (moved('payment_method')) return says(`changed how ${visit} was paid to ${str(now('payment_method')) || 'not known'}`, 'payment_method')
    // Send all payment texts (0093): a reminder Roundly texted, and one it put back because the text didn't arrive.
    if (now('_chase_send')) {
      const kind = str(now('_chase_kind'))
      const which = kind === 'payment' ? 'the payment message' : /^chase_[123]$/.test(kind) ? `reminder ${kind.slice(-1)}` : 'a reminder'
      return says(`texted ${customer ?? 'the customer'} ${which} from Roundly for the ${when ? `${when} ` : ''}visit`, 'chased', 'chase_count', 'chased_on', 'chased_at', 'wa_attempt')
    }
    if (now('_chase_back')) return says(`put a reminder back on ${visit}: Roundly's text didn't arrive`, 'chased', 'chase_count', 'chased_on', 'chased_at', 'wa_attempt')
    const countWas = Number(was('chase_count') ?? 0)
    const countNow = Number(now('chase_count') ?? 0)
    if ((moved('chase_count') && countNow > countWas) || (moved('chased') && now('chased') === 1)) {
      return says(`chased ${customer ?? 'the customer'} for the ${when ? `${when} ` : ''}visit${countNow > 1 ? ` (chase ${countNow})` : ''}`, 'chased', 'chase_count', 'chased_on')
    }
    if (moved('chase_count') || moved('chased')) return says(`took back a chase on ${visit}`, 'chased', 'chase_count', 'chased_on')
    if (moved('worker')) {
      const to = str(now('worker'))
      return says(to ? `gave ${visit} to ${to}` : `took ${str(was('worker')) || 'the worker'} off ${visit}`, 'worker')
    }
    if (moved('confirmed')) return says(now('confirmed') === 1 ? `marked ${visit} confirmed` : `marked ${visit} not confirmed`, 'confirmed')
    if (moved('amount_pence')) {
      const from = pounds(was('amount_pence'))
      return says(from ? `changed the price of ${visit} from ${from} to ${pounds(now('amount_pence')) || 'blank'}` : `set the price of ${visit} to ${pounds(now('amount_pence'))}`, 'amount_pence')
    }
    if (moved('blocked_reason')) {
      const r = blockedReasonLabel(str(now('blocked_reason')))
      return says(r ? `recorded that ${visit} couldn't be done: ${lower(r)}` : `cleared "couldn't do it" on ${visit}`, 'blocked_reason', 'blocked_note', 'blocked_at')
    }
    // A time put right afterwards (Change times, Started a while ago: 0083) reads as a change, not a second start.
    if (moved('started_at')) return says(blank(now('started_at')) ? `undid the start of ${visit}` : blank(was('started_at')) ? `started ${visit}` : `changed the start time of ${visit}`, 'started_at', 'finished_at', 'started_how', 'finished_how')
    if (moved('finished_at')) return says(blank(now('finished_at')) ? `undid the finish on ${visit}` : blank(was('finished_at')) ? `finished ${visit}` : `changed the finish time of ${visit}`, 'finished_at', 'finished_how', 'cash_collected_pence')
    if (moved('started_how') || moved('finished_how')) return says(`changed how the times of ${visit} were recorded`, 'started_how', 'finished_how')
    if (moved('reviewed_on')) return says(blank(now('reviewed_on')) ? `put ${visit} back in Past bookings to review` : `said ${visit} didn't happen`, 'reviewed_on')
    if (moved('time_slot') || moved('duration_minutes')) {
      const t = setTimesOn() ? str(now('time_slot')) : ''
      return says(moved('time_slot') && t ? `changed the time of ${visit} to ${shownTime(t)}` : `changed the time of ${visit}`, 'time_slot', 'duration_minutes')
    }
    if (moved('sort_order')) return says(`moved ${customer ?? 'a visit'} in the running order${when ? ` on ${when}` : ''}`, 'sort_order')
    if (moved('note')) return says(blank(was('note')) ? `added a note to ${visit}` : blank(now('note')) ? `cleared the note on ${visit}` : `changed the note on ${visit}`, 'note')
    const fields = fieldChanges(action, before, after).map((c) => c.field)
    return fields.length ? says(`changed the ${other(fields.slice(0, 2))} on ${visit}`, ...fields.slice(0, 2)) : whole(`saved ${visit} with no changes`)
  }

  if (entity === 'customers') {
    const removed = row.removed && typeof row.removed === 'object' ? (row.removed as Record<string, unknown>) : null
    const name = str(row.name) || str(removed?.name) || names.customers[String(entityId)] || 'a customer'
    const their = possessive(name)
    if (action === 'create') return whole(`added ${name} as a customer`)
    if (action === 'delete' || (moved('deleted_at') && !blank(now('deleted_at')))) return whole(`removed ${name} from customers`)
    if (moved('name')) return says(`renamed ${str(was('name')) || 'a customer'} to ${str(now('name'))}`, 'name')
    if (moved('reg_price_pence')) {
      const from = pounds(was('reg_price_pence'))
      return says(from ? `changed ${their} price from ${from} to ${pounds(now('reg_price_pence')) || 'blank'}` : `set ${their} price to ${pounds(now('reg_price_pence'))}`, 'reg_price_pence')
    }
    if (moved('cycle_days')) {
      const c = cycleWords(now('cycle_days'))
      return says(c ? `changed how often ${name} is done to ${c}` : `cleared how often ${name} is done`, 'cycle_days', 'cycle_text')
    }
    if (moved('status') || moved('message_reply') && now('message_reply') === 'stopped') return says(`marked ${name} ${now('status') === 'paused' && now('message_reply') === 'stopped' ? 'stopped' : str(now('status')) || 'active'}`, 'status', 'message_reply')
    if (moved('rebook_messages') && Number(now('rebook_messages') ?? 0) > Number(was('rebook_messages') ?? 0)) return says(`sent ${name} a rebook message`, 'rebook_messages', 'rebook_messaged_on')
    if (moved('follow_up_on')) {
      const d = day(now('follow_up_on'))
      return says(d ? `set a follow-up with ${name} for ${d}` : `cleared the follow-up with ${name}`, 'follow_up_on')
    }
    if (moved('last_contacted_on') || moved('contact_note')) return says(`recorded contact with ${name}`, 'last_contacted_on', 'contact_note')
    if (moved('to_call')) return says(now('to_call') === 1 ? `put ${name} on the call list` : `took ${name} off the call list`, 'to_call')
    // The exact spot alone (27 Oct 2026, exact-spot.ts): said as a pin, not as a new address.
    if (moved('maps_link') && !moved('address') && !moved('postcode')) return says(now('maps_link') ? `pinned ${their} exact spot` : `took the pin off ${their} exact spot`, 'maps_link')
    if (moved('address') || moved('postcode') || moved('maps_link')) return says(`changed ${their} address`, 'address', 'postcode', 'maps_link', 'latitude', 'longitude', 'geocoded_postcode')
    if (moved('phone')) return says(`changed ${their} phone number`, 'phone')
    // How they are messaged, and "The postcode is enough" (tidy-up 2 item 3, 20 Oct 2026: it read "changed Joel's message by").
    if (moved('message_by')) {
      const to = str(now('message_by'))
      return says(to === 'none' ? `set ${name} to Don't message` : to ? `changed how ${name} is messaged to ${WAY_WORDS[to] ?? to}` : `put ${name} back to your usual way of messaging`, 'message_by')
    }
    if (moved('commercial')) return says(Number(now('commercial')) === 1 ? `made ${name} commercial: invoiced after each job` : `made ${name} not commercial`, 'commercial')
    if (moved('postcode_enough')) return says(Number(now('postcode_enough')) === 1 ? `said ${postcodeWords('the postcode')} is enough for ${name}` : `asked for ${their} street again`, 'postcode_enough')
    if (moved('latitude') || moved('longitude')) return says(`placed ${name} on the map`, 'latitude', 'longitude', 'geocoded_postcode')
    const fields = fieldChanges(action, before, after).map((c) => c.field)
    if (fields.length === 0) return whole(`saved ${their} details with no changes`)
    if (fields.length > 2) return whole(`changed ${plural(fields.length, 'thing')} on ${their} details`)
    return says(`changed ${their} ${other(fields)}`, ...fields)
  }

  if (entity === 'leads') {
    if (moved('regular_offer_choice_id')) return whole(row.regular_offer_choice_id ? 'saved their cleaning choice' : 'undid their cleaning choice')
    const name = str(row.name) || 'a new job'
    if (action === 'create') return whole(`added ${name} to New jobs`)
    if (action === 'delete' || (moved('deleted_at') && !blank(now('deleted_at')))) return whole(`removed ${name} from New jobs`)
    if (moved('converted_customer_id') && !blank(now('converted_customer_id'))) return says(`made ${name} a customer`, 'converted_customer_id', 'status', 'status_raw')
    if (moved('converted_customer_id')) return says(`unlinked ${name} from a customer`, 'converted_customer_id')
    if (moved('status_raw') || moved('status')) {
      const to = str(now('status_raw')) || str(now('status'))
      return says(to ? `moved ${name} to ${to}` : `moved ${name} back to New`, 'status', 'status_raw')
    }
    if (moved('enquiry_messages')) return says(`sent ${name} a message`, 'enquiry_messages', 'enquiry_messaged_on')
    if (moved('initial_pence') || moved('regular_pence') || moved('quoted_on') || moved('cycle_weeks')) return says(`changed the quote for ${name}`, 'initial_pence', 'regular_pence', 'quoted_on', 'cycle_weeks')
    if (moved('follow_up_on')) {
      const d = day(now('follow_up_on'))
      return says(d ? `set a follow-up with ${name} for ${d}` : `cleared the follow-up with ${name}`, 'follow_up_on')
    }
    if (moved('last_contacted_on') || moved('contact_note')) return says(`recorded contact with ${name}`, 'last_contacted_on', 'contact_note')
    const fields = fieldChanges(action, before, after).map((c) => c.field)
    if (fields.length > 2) return whole(`changed ${plural(fields.length, 'thing')} on ${name}`)
    return fields.length ? says(`changed the ${other(fields)} on ${name}`, ...fields) : whole(`saved ${name} with no changes`)
  }

  if (entity === 'users') {
    const email = str(row.email)
    const nameOf = (r: Record<string, unknown> | null): string => str(r?.worker_name)
    const person = nameOf(after) || nameOf(before) || names.people[email.toLowerCase()] || email || 'someone'
    if ('photo' in row) {
      const had = before?.photo === true
      return whole(after?.photo === false ? 'removed their photo' : had ? 'changed their photo' : 'added their photo')
    }
    if (action === 'create') {
      const withEmail = nameOf(after) && email ? `${person} (${email})` : person
      if (row.role === 'owner') return whole(`added ${withEmail} as an owner`)
      return whole(`added ${withEmail} as a worker${row.show_prices === 0 ? ', with prices hidden' : ''}`)
    }
    if (action === 'delete') return whole(`removed ${person} from People`)
    if (moved('role')) return says(now('role') === 'owner' ? `made ${person} an owner` : `made ${person} a worker${moved('show_prices') && now('show_prices') === 0 ? ', with prices hidden' : ''}`, 'role', 'show_prices')
    if (moved('show_prices')) return says(now('show_prices') === 0 ? `hid prices from ${person}` : `let ${person} see prices`, 'show_prices')
    if (moved('worker_name')) {
      const from = str(was('worker_name'))
      const to = str(now('worker_name'))
      const whose = email || person
      if (!from) return says(`put the name ${to} on the visits for ${whose}`, 'worker_name')
      if (!to) return says(`took the name ${from} off ${whose}`, 'worker_name')
      return says(`changed the name on the visits for ${whose} from ${from} to ${to}`, 'worker_name')
    }
    if (moved('email')) return says(`changed the sign-in email for ${nameOf(after) || str(was('email'))} to ${str(now('email'))}`, 'email')
    const fields = fieldChanges(action, before, after).map((c) => c.field)
    return fields.length ? says(`changed the ${other(fields.slice(0, 2))} for ${person}`, ...fields.slice(0, 2)) : whole(`saved ${person} with no changes`)
  }

  if (entity === 'round_days') {
    const when = day(row.date) || 'a day'
    if (action === 'create') return whole(`set ${when} as ${str(row.worker) || 'someone'}'s day`)
    if (action === 'delete') return whole(`cleared ${str(row.worker) || 'the'}${row.worker ? "'s" : ''} day on ${when}`)
    if (moved('worker')) return says(`changed ${when} from ${str(was('worker'))}'s day to ${str(now('worker'))}'s`, 'worker')
  }

  // A worker's repeating day (0040): "set every Friday as Sendi's day".
  if (entity === 'work_day_rules') {
    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const every = typeof row.weekday === 'number' ? `every ${weekdays[row.weekday] ?? 'week'}` : 'a repeating day'
    const whose = str(row.worker) || 'someone'
    if (action === 'create') return whole(`set ${every} as ${whose}'s day`)
    if (action === 'delete') return whole(`stopped ${every} as ${whose}'s day`)
    return whole(`changed ${whose}'s ${every}`)
  }

  if (entity === 'day_notes') {
    const when = day(row.date) || 'a day'
    if (action === 'create') return whole(`added a note for ${when}`)
    if (action === 'delete') return whole(`removed the note for ${when}`)
    return whole(`changed the note for ${when}`)
  }

  if (entity === 'day_ledger') {
    const when = day(row.date) || 'a day'
    const worker = str(row.worker)
    const forWho = worker ? ` for ${worker}` : ''
    if (action === 'create') return whole(`closed out ${when}${forWho}`)
    if (action === 'delete') return whole(`removed the close-out${forWho} on ${when}`)
    return whole(`changed the close-out${forWho} on ${when}`)
  }

  if (entity === 'media') {
    const customer = names.customers[str(row.customer_id)]
    const thing = row.kind === 'audio' ? 'a voice note' : 'a photo'
    const forWho = customer ? ` for ${customer}` : ''
    if (action === 'create') return whole(`added ${thing}${forWho}`)
    if (action === 'delete' || (moved('deleted_at') && !blank(now('deleted_at')))) return whole(`deleted ${thing}${forWho}`)
    return whole(`changed ${thing}${forWho}`)
  }

  if (entity === 'gutter_jobs') {
    const customer = names.customers[str(row.customer_id)]
    const forWho = customer ? ` for ${customer}` : ''
    if (action === 'create') return whole(`added a gutter job${forWho}`)
    if (action === 'delete') return whole(`removed a gutter job${forWho}`)
    return whole(`changed a gutter job${forWho}`)
  }

  if (entity === 'business_settings') {
    // Money from Squeegee (0043): switched on by accepting the money check on a backup import, or by Liam.
    if (moved('brought_money')) return says(now('brought_money') === 1 ? 'turned on money from Squeegee for the round' : 'turned off money from Squeegee for the round', 'brought_money', 'import_batch_id')
    const SETTING: Record<string, string> = {
      business_name: 'the business name',
      short_name: 'the round name',
      chase_signoff: 'the name on payment chases',
      booking_signoff: 'the name on booking messages',
      bank_lines: 'the bank details',
      blocks_enfield: 'the Enfield rule',
    }
    const fields = fieldChanges(action, before, after).map((c) => c.field)
    const said = fields.map((f) => SETTING[f] ?? `the ${lower(fieldLabel(f))}`)
    return whole(said.length ? `changed ${said.slice(0, 2).join(' and ')}${said.length > 2 ? ` and ${plural(said.length - 2, 'other setting')}` : ''} in Your business` : 'saved Your business with no changes')
  }

  if (entity === 'businesses') {
    const name = str(row.name) || 'the business'
    if (action === 'create') return whole(`set up ${name} on Roundly`)
    if (moved('status')) return says(`changed ${name} to ${str(now('status'))}`, 'status')
  }

  return whole(`${lower(actionWord(action))} ${lower(entityLabel(entity))} ${recordTitle(entity, entityId, before, after)}`)
}

// Undo on History (Liam, 9 Oct 2026: "should history have a undo button? ... yes add undo button"). A change to a visit
// made by a person in the last day is put back the way the Undo bar puts one back: one save of the values it had
// before, guarded by the version that change left, so a visit changed since is refused, never overwritten. The server
// does the rest as for any change: a Done undone takes away the visit it booked next, a move undone brings back its
// door record. Only the newest change to a visit is offered (the screen decides that). Customer changes, visits made
// or removed, changes by Roundly itself and anything older than a day are not offered.
export const UNDO_WINDOW_HOURS = 24
// Extras and insides go back with the price they sit inside (0087; Sonnet's extras review 4).
const UNDO_FIELDS = ['breakdown_json', 'date', 'amount_pence', 'inside_pence', 'extras_json', 'kind', 'state', 'payment_status', 'payment_method', 'confirmed', 'chased', 'chase_count', 'chased_on', 'time_slot', 'time_agreed_for', 'duration_minutes', 'sort_order', 'worker', 'note', 'blocked_reason', 'blocked_note', 'reviewed_on', 'started_at', 'finished_at', 'started_how', 'finished_how', 'outsides_done_at'] as const

// An enquiry's own fields that Undo puts back (Liam, 5 Nov 2026: "i didnt send the email in the end but when i came back
// onto the page this didnt say undo and then i came to history and there was no undo button?"). Its let-go time, reason
// and archive follow its status on the server (functions/api/leads/[id].ts), so they are not sent.
const LEAD_UNDO_FIELDS = ['status_raw', 'contact_note', 'last_contacted_on', 'follow_up_on', 'enquiry_messages', 'enquiry_messaged_on', 'quoted_on', 'initial_pence', 'regular_pence', 'cycle_weeks', 'cycle_text', 'notes', 'checked', 'message_by'] as const

function leadUndoBody(ev: AuditEvent, now: Date): Record<string, unknown> | null {
  if (ev.entity !== 'leads' || ev.action !== 'update' || !ev.actor.includes('@')) return null
  const at = Date.parse(`${ev.created_at.replace(' ', 'T')}Z`)
  if (!Number.isFinite(at) || now.getTime() - at > UNDO_WINDOW_HOURS * 3_600_000 || at - now.getTime() > 3_600_000) return null
  const before = parseRow(ev.before_json), after = parseRow(ev.after_json)
  if (!before || !after || typeof after.version !== 'number') return null
  // An enquiry made a customer is put back from the customer, never here.
  if (after.converted_customer_id != null && before.converted_customer_id == null) return null
  const body: Record<string, unknown> = { version: after.version }
  for (const f of LEAD_UNDO_FIELDS) if (JSON.stringify(before[f] ?? null) !== JSON.stringify(after[f] ?? null)) body[f] = before[f] ?? null
  return Object.keys(body).length === 1 ? null : body
}

export function undoBodyFor(ev: AuditEvent, now: Date): Record<string, unknown> | null {
  if (ev.entity === 'leads') return ev.on_invoice ? null : leadUndoBody(ev, now)
  if (ev.on_invoice || ev.entity !== 'cleans' || ev.action !== 'update' || !ev.actor.includes('@')) return null
  const at = Date.parse(`${ev.created_at.replace(' ', 'T')}Z`)
  if (!Number.isFinite(at) || now.getTime() - at > UNDO_WINDOW_HOURS * 3_600_000 || at - now.getTime() > 3_600_000) return null
  const before = parseRow(ev.before_json)
  const after = parseRow(ev.after_json)
  if (!before || !after || typeof after.version !== 'number') return null
  if ('_breakdown_rev' in after && !('breakdown_json' in before || 'breakdown_json' in after)) return null
  // A reminder Roundly texted can't be taken back (Send all, 0093): no Undo offered for it, or for its put back.
  if (after._chase_send || after._chase_back || after._customer_offer) return null
  const body: Record<string, unknown> = { version: after.version }
  for (const f of UNDO_FIELDS) {
    // Not done clears presentation. Undo may mark the work done again, but the
    // cleared breakdown stays cleared; it is only saved from a completed invoice step.
    if (f === 'breakdown_json' && after.state && after.state !== 'done') continue
    if (!(f in before) && !(f in after)) continue
    if (JSON.stringify(before[f] ?? null) !== JSON.stringify(after[f] ?? null)) body[f] = before[f] ?? null
  }
  // A job time is stored without its zone (UTC) and the server takes one only with it: a time put right with Change times
  // (0083) is put back as it was.
  for (const f of ['started_at', 'finished_at', 'outsides_done_at']) {
    const v = body[f]
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(v)) body[f] = `${v.replace(' ', 'T')}Z`
  }
  // Back to a visit not done: payment and chase only mean something on a done visit, and the server clears them itself
  // when a visit stops being done (as Owed's "This visit wasn't done" sends the state alone).
  if (body.state !== undefined && body.state !== 'done') for (const f of ['payment_status', 'payment_method', 'chased', 'chase_count', 'chased_on']) delete body[f]
  if (Object.keys(body).length === 1) return null
  if ('breakdown_json' in body) {
    if (typeof after._breakdown_rev !== 'number') return null
    body.breakdown_money_rev = after._breakdown_rev
    // Undo itemisation/price only. The planner derives the current payment state and
    // method from live allocations and their receipts, including shared bank payments.
    delete body.payment_status; delete body.payment_method
    if (before.amount_pence !== after.amount_pence && Number(after._breakdown_paid) > 0) body.breakdown_confirm = { paid_pence: Number(after._breakdown_paid), total_pence: Number(before.amount_pence) }
  }
  if ('date' in body) body.undo_restore = true
  return body
}
